/**
 * Gemeinsame Typen fuer API, Scanner, Banner und Dashboard.
 * Diese Datei ist die Source of Truth fuer alles, was ueber die Netzwerkgrenze geht.
 */

/** Die vier Kategorien, die sich in der EU-Praxis durchgesetzt haben (analog Cookiebot). */
export const CONSENT_CATEGORIES = ['necessary', 'preferences', 'statistics', 'marketing'] as const;
export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

/** Kategorie plus "unclassified" fuer alles, was der Scanner noch nicht einordnen konnte. */
export type CookieCategory = ConsentCategory | 'unclassified';

/** Zustimmungszustand pro Kategorie. "necessary" ist immer true, das ist keine Einwilligung. */
export type ConsentState = Record<ConsentCategory, boolean>;

export type ConsentMethod =
  | 'accept_all'
  | 'accept_selection'
  | 'reject_all'
  | 'withdrawn'
  | 'imported';

/** Speichertechnik, die der Scanner gefunden hat. */
export type StorageType = 'cookie' | 'localStorage' | 'sessionStorage' | 'indexedDB' | 'pixel';

/** Ein einzelner erkannter Eintrag (Cookie oder aehnliche Technik). */
export interface CookieEntry {
  /** Name des Cookies bzw. Storage-Keys, wie er tatsaechlich gesetzt wurde. */
  name: string;
  /** Domain, die den Eintrag gesetzt hat, z. B. ".google.de". */
  domain: string;
  path?: string;
  type: StorageType;
  category: CookieCategory;
  /** Zweckbeschreibung fuer die Cookie-Erklaerung, Sprache des Mandanten. */
  purpose: string;
  /** Anbietername, z. B. "Google LLC". Leer, wenn unbekannt. */
  vendor?: string;
  /** Link zur Datenschutzerklaerung des Anbieters. */
  vendorPrivacyUrl?: string;
  /**
   * Laufzeit in Sekunden, wie vom Browser gemeldet.
   * null bedeutet Session-Cookie bzw. nicht ermittelbar.
   */
  maxAgeSeconds: number | null;
  /** true, wenn der Eintrag von einer Fremddomain gesetzt wurde. */
  thirdParty: boolean;
  secure?: boolean;
  httpOnly?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None' | null;
  /** Wo der Eintrag zuerst beobachtet wurde. */
  foundOnUrl?: string;
  /** Woher die Einordnung stammt. Relevant fuer die Pruefung durch den Admin. */
  classificationSource?: 'knowledge_base' | 'heuristic' | 'manual' | 'unknown';
  /** 0 bis 1. Unter 0.6 sollte der Admin gegenlesen. */
  confidence?: number;
}

/** Ein Drittanbieter-Dienst, der auf der Seite geladen wird. */
export interface DetectedVendor {
  key: string;
  name: string;
  category: CookieCategory;
  /** Host-Muster, die zu diesem Anbieter gehoeren. */
  hosts: string[];
  privacyUrl?: string;
  /** Sitz des Anbieters, relevant fuer Drittlandtransfer (Art. 44 ff. DSGVO). */
  country?: string;
}

/** Design- und Textkonfiguration des Banners. */
export interface BannerConfig {
  layout: 'bar_bottom' | 'bar_top' | 'box_bottom_left' | 'box_bottom_right' | 'dialog_center';
  theme: 'light' | 'dark' | 'auto';
  /** true blendet den Rest der Seite aus, bis entschieden wurde. */
  blocking: boolean;
  showRejectButton: boolean;
  /**
   * true = Annehmen und Ablehnen sehen identisch aus.
   *
   * Hintergrund: die Aufsichtsbehoerden (EDPB Cookie Banner Taskforce 2023,
   * Beschluesse der DSK) beanstanden Banner, bei denen die Ablehnung optisch
   * benachteiligt ist, etwa als blasser Button oder als blosser Textlink.
   * Deshalb ist die Gleichbehandlung hier der Standard. Wer das abschaltet,
   * trifft eine bewusste Entscheidung und traegt das Risiko.
   */
  equalChoiceStyling: boolean;
  showLogoUrl?: string;
  colors: {
    background: string;
    foreground: string;
    primary: string;
    onPrimary: string;
    secondary: string;
    border: string;
  };
  cornerRadius: number;
  /** Position des kleinen Wiederaufruf-Buttons nach der Entscheidung. */
  badge: 'bottom_left' | 'bottom_right' | 'top_left' | 'top_right' | 'hidden';
  /**
   * Abstand des Buttons zum Bildschirmrand in Pixeln.
   *
   * Noetig, weil viele Websites in einer Ecke bereits etwas haben: ein
   * Chat-Fenster, einen Nach-oben-Pfeil, eine Telefonschaltflaeche. Ohne
   * Verschiebemoeglichkeit liegen zwei Elemente uebereinander und eines ist
   * nicht mehr erreichbar.
   */
  badgeOffsetX?: number;
  badgeOffsetY?: number;
  /** Animationen komplett aus, falls der Kunde das will. */
  animations: boolean;
  /**
   * Herkunftsangabe mit Maskottchen und Link im Banner.
   *
   * Sichtbar zu machen, woher ein Einwilligungsbanner stammt, schafft
   * Vertrauen und ist zugleich Werbung. Im freien Tarif bleibt sie an, in
   * bezahlten Tarifen kann sie abgeschaltet werden.
   */
  showBranding: boolean;
}

export interface BannerTexts {
  title: string;
  body: string;
  acceptAll: string;
  rejectAll: string;
  acceptSelection: string;
  settings: string;
  close: string;
  categoriesTitle: string;
  poweredBy?: string;
  imprintUrl?: string;
  imprintLabel?: string;
  privacyUrl?: string;
  privacyLabel?: string;
  categoryLabels: Record<ConsentCategory, { label: string; description: string }>;
  /** Texte fuer den Platzhalter blockierter Einbettungen (YouTube, Maps, ...). */
  blockedContent?: { title: string; body: string; button: string };
}

/**
 * Das Objekt, das der Banner beim Seitenaufruf zieht.
 * Wird am Edge gecacht, muss klein und ohne Personenbezug sein.
 */
export interface SiteRuntimeConfig {
  siteId: string;
  /** Erhoehen, wenn sich Zwecke aendern. Erzwingt erneute Einwilligung. */
  consentVersion: number;
  /** Sprache als Fallback, wenn der Browser nichts Passendes meldet. */
  defaultLanguage: string;
  languages: Record<string, BannerTexts>;
  banner: BannerConfig;
  /** Gueltigkeitsdauer der Einwilligung in Tagen. EU-Praxis: 365 oder weniger. */
  consentLifetimeDays: number;
  /** Google Consent Mode v2 ansteuern. */
  googleConsentMode: boolean;
  /** true = Google Consent Mode im "advanced" Modus (Pings vor Einwilligung). */
  googleConsentModeAdvanced: boolean;
  /** Host-Muster, die vor Einwilligung blockiert werden, pro Kategorie. */
  blockRules: Array<{ pattern: string; category: ConsentCategory }>;
  /** Endpunkt fuer das Consent-Log. */
  logEndpoint: string;
  /** Endpunkt fuer die Cookie-Erklaerung (wird nur bei Bedarf geladen). */
  declarationEndpoint: string;
  /** Ziel des Herkunftslinks im Banner. Leer bedeutet: keine Verlinkung. */
  brandUrl?: string;
  /** Adresse des Maskottchens. Leer bedeutet: nur der Name, ohne Bild. */
  brandIconUrl?: string;
  /** Cookie-Erklaerung, optional eingebettet ueber data-ilc-declaration. */
  declaration?: { updatedAt: string; entries: CookieEntry[] };
}

/** Was der Banner an das Log sendet. Enthaelt bewusst keine Klardaten der Person. */
export interface ConsentLogPayload {
  siteId: string;
  /** Vom Client erzeugte, zufaellige ID. Ist der Nachweisschluessel fuer den Besucher. */
  consentId: string;
  consentVersion: number;
  state: ConsentState;
  method: ConsentMethod;
  /** Seite, auf der die Entscheidung gefallen ist. Ohne Query-String. */
  pageUrl: string;
  language: string;
  /** Millisekunden zwischen Anzeige und Entscheidung. Belegt, dass nicht vorangekreuzt war. */
  decisionMs?: number;
  /** Der im Browser gespeicherte String, exakt so wie gesetzt. */
  consentString: string;
}

export interface ScanRequest {
  scanId: string;
  siteId: string;
  startUrl: string;
  maxPages: number;
  /** Zusaetzliche Pfade, die immer mitgescannt werden sollen. */
  includePaths?: string[];
  callbackUrl: string;
  callbackToken: string;
}

export interface ScanResult {
  scanId: string;
  siteId: string;
  status: 'completed' | 'failed';
  error?: string;
  startedAt: string;
  finishedAt: string;
  pagesScanned: string[];
  /**
   * Zahl der gefundenen eigenen Unterseiten, auch der nicht besuchten.
   *
   * Das ist die Groesse, nach der abgerechnet wird. Sie ist nach unten
   * verzerrt, weil nur Verweise auf bereits besuchten Seiten sichtbar sind.
   * Eine Verzerrung zugunsten des Kunden ist die richtige Richtung.
   */
  pagesDiscovered?: number;
  entries: CookieEntry[];
  vendors: DetectedVendor[];
  /** Externe Requests, die der Scanner gesehen hat. Basis fuer Blocking-Regeln. */
  thirdPartyHosts: string[];
}
